// src/context/UserContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import axiosInstance, { setHasActiveSession } from '../services/AxiosInstance';
import socket from '../utils/socketClient';
import { isDeferring, setPendingRefresh } from '../utils/saldoControl';

const UserContext = createContext(null);

// Duración de la sesión en el backend (debe coincidir con cookie.maxAge del servidor)
const SESSION_DURATION = 60 * 60 * 1000; // 1 hora
// Clave para persistir la fecha de expiración entre recargas
const SESSION_EXPIRY_KEY = 'session-expires-at';

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasAttemptedFetch = useRef(false);
  const userDataRef = useRef(null);
  const sessionTimerRef = useRef(null);

  // Mantener sincronizado el ref con el estado
  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  const fetchUserData = useCallback(async () => {
    // Evitar múltiples llamadas simultáneas
    if (hasAttemptedFetch.current) {
      return;
    }

    hasAttemptedFetch.current = true;
    setIsLoading(true);

    try {
      const response = await axiosInstance.get('/api/auth/status', {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      if (response.data?.authenticated === false) {
        setUserData(null);
        setHasActiveSession(false);
      } else {
        setUserData(response.data);
        setHasActiveSession(true);
      }
    } catch (error) {
      setUserData(null);
      setHasActiveSession(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateUserData = useCallback((newData) => {
    setUserData(newData);
  }, []);

  const clearUserData = useCallback(() => {
    setUserData(null);
    setHasActiveSession(false);
    hasAttemptedFetch.current = false;
    // Limpiar timer de sesión
    if (sessionTimerRef.current) {
      clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }
    sessionStorage.removeItem(SESSION_EXPIRY_KEY);
  }, []);

  const refreshUserData = useCallback(async () => {
    hasAttemptedFetch.current = false;
    await fetchUserData();
  }, [fetchUserData]);

  const silentRefreshUserData = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/auth/status', {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      if (response.data?.authenticated === false) {
        setUserData(null);
        setHasActiveSession(false);
      } else {
        setUserData(response.data);
      }
    } catch (error) {
      // Silencioso - no hacer nada en caso de error
    }
  }, []);

  // Permite descontar visualmente el saldo en el SessionBar durante animaciones de juego
  const debitSaldo = useCallback((amount) => {
    setUserData((prev) =>
      prev ? { ...prev, saldo: prev.saldo - amount } : prev
    );
  }, []);

  // Permite actualizar el saldo directamente desde los datos del juego sin HTTP request
  const setSaldoDirecto = useCallback((saldo) => {
    setUserData((prev) => (prev ? { ...prev, saldo } : prev));
  }, []);

  // Cargar datos del usuario al montar
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Conectar/desconectar socket según estado de autenticación
  useEffect(() => {
    if (userData) {
      if (!socket.connected) {
        socket.connect();
      }
    } else {
      if (socket.connected) {
        socket.disconnect();
      }
    }
  }, [userData]);

  // Escuchar actualizaciones de socket (solo se ejecuta UNA VEZ al montar)
  useEffect(() => {
    const handleUserUpdate = (updatedUser) => {
      if (updatedUser.id === userDataRef.current?.id) {
        updateUserData(updatedUser);
      }
    };

    const handlePaymentUpdate = (data) => {
      if (data.userId === userDataRef.current?.id) {
        refreshUserData();
      }
    };

    const handleSaldoUpdate = (data) => {
      if (data.userId === userDataRef.current?.id) {
        if (isDeferring()) {
          setPendingRefresh(() => {
            setUserData((prev) =>
              prev ? { ...prev, saldo: data.nuevoSaldo } : prev
            );
          });
          return;
        }
        setUserData((prev) =>
          prev ? { ...prev, saldo: data.nuevoSaldo } : prev
        );
      }
    };

    socket.on('actualizacion-usuario', handleUserUpdate);
    socket.on('actualizacion-pago', handlePaymentUpdate);
    socket.on('actualizacion-saldo', handleSaldoUpdate);

    return () => {
      socket.off('actualizacion-usuario', handleUserUpdate);
      socket.off('actualizacion-pago', handlePaymentUpdate);
      socket.off('actualizacion-saldo', handleSaldoUpdate);
    };
  }, [updateUserData, refreshUserData]);

  // Escuchar evento session-expired para limpiar usuario inmediatamente
  useEffect(() => {
    const handleSessionExpired = () => {
      setUserData(null);
      setHasActiveSession(false);
      hasAttemptedFetch.current = false;
      // Limpiar timer
      if (sessionTimerRef.current) {
        clearTimeout(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
      sessionStorage.removeItem(SESSION_EXPIRY_KEY);
    };

    window.addEventListener('session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, []);

  /**
   * Inicia (o reinicia) el timer de expiración de sesión.
   * Calcula el tiempo restante a partir de la fecha de expiración guardada
   * o crea una nueva expiración basada en SESSION_DURATION.
   */
  const startSessionTimer = useCallback((expiresAt) => {
    // Limpiar timer previo
    if (sessionTimerRef.current) {
      clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }

    const now = Date.now();
    let expiry = expiresAt;

    if (!expiry) {
      // Primera vez: calcular expiración y guardarla
      expiry = now + SESSION_DURATION;
      sessionStorage.setItem(SESSION_EXPIRY_KEY, expiry.toString());
    }

    const remaining = expiry - now;

    if (remaining <= 0) {
      // Ya expiró
      setHasActiveSession(false);
      window.dispatchEvent(new Event('session-expired'));
      return;
    }

    sessionTimerRef.current = setTimeout(() => {
      sessionStorage.removeItem(SESSION_EXPIRY_KEY);
      setHasActiveSession(false);
      window.dispatchEvent(new Event('session-expired'));
    }, remaining);
  }, []);

  // Timer proactivo de expiración de sesión por inactividad
  useEffect(() => {
    if (!userData) {
      // Sin usuario: limpiar timer
      if (sessionTimerRef.current) {
        clearTimeout(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
      return;
    }

    // Intentar recuperar expiración guardada (sobrevive recargas de página)
    const savedExpiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
    const expiresAt = savedExpiry ? parseInt(savedExpiry, 10) : null;

    startSessionTimer(expiresAt);

    // Escuchar actividad (requests exitosas) para reiniciar el timer
    const handleActivity = () => {
      // Reiniciar desde cero: ahora + SESSION_DURATION
      const newExpiry = Date.now() + SESSION_DURATION;
      sessionStorage.setItem(SESSION_EXPIRY_KEY, newExpiry.toString());
      startSessionTimer(newExpiry);
    };

    window.addEventListener('session-activity', handleActivity);

    return () => {
      window.removeEventListener('session-activity', handleActivity);
      if (sessionTimerRef.current) {
        clearTimeout(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
    };
  }, [userData, startSessionTimer]);

  const value = {
    userData,
    isLoading,
    updateUserData,
    clearUserData,
    refreshUserData,
    debitSaldo,
    setSaldoDirecto,
    silentRefreshUserData,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser debe ser usado dentro de un UserProvider');
  }
  return context;
};
